import useSWR from 'swr';
import { validate as validateUUID } from 'uuid';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getUserById } from '@/services/patientService.ts';

interface PatientHook {
    patient: GenericPatient | undefined;
    is_loading: boolean;
    error: Error | undefined;
}

interface UsePatientParams {
    user_id?: string;
    instance_id?: string;
    pvs_provider?: PVSProvider;
    /** Override eterno_user_id from the caller context (e.g. appointment.user_id),
     *  because getUserById does not return is_registered or eterno_user_id for DocCirrus patients. */
    eterno_user_id?: string;
}

const usePatient = ({ user_id, instance_id, pvs_provider, eterno_user_id }: UsePatientParams): PatientHook => {
    const {
        data: fetchedPatient,
        isLoading: is_loading,
        error,
    }: {
        data: GenericPatient | undefined;
        isLoading: boolean;
        error: Error | undefined;
    } = useSWR(
        user_id && instance_id && pvs_provider
            ? {
                  key: 'patient',
                  instance_id: instance_id,
                  user_id: user_id,
              }
            : undefined,
        () => getUserById<GenericPatient>(user_id!, instance_id!, pvs_provider!)
    );

    // getUserById (DocCirrus) does not return eterno_user_id or is_registered — the caller
    // provides the authoritative eterno_user_id from the context that already has it (appointment, search result).
    // We need to set is_registered to true if we have an eterno_user_id, because that means the patient is registered.
    const patient: GenericPatient | undefined =
        fetchedPatient && eterno_user_id && validateUUID(eterno_user_id)
            ? {
                  ...fetchedPatient,
                  eterno_user_id: eterno_user_id,
                  is_registered: true,
              }
            : fetchedPatient;

    return { patient, is_loading, error };
};

export default usePatient;
